import { z } from "zod";
import { AGENT_ROLES, TASK_TYPES } from "./enums.ts";

export const SKILL_NAME_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
export const SKILL_NAME_MAX_CHARS = 64;
export const SKILL_DESCRIPTION_MAX_CHARS = 1024;
export const SKILL_BODY_MAX_BYTES = 64 * 1024;
export const SKILL_ASSETS_PER_VERSION_MAX = 64;
export const SKILL_ASSET_MAX_BYTES = 256 * 1024;
export const SKILL_PACKAGE_MAX_BYTES = 2 * 1024 * 1024;
export const SKILL_BATCH_IMPORT_MAX = 50;
export const SKILL_RESOURCE_READ_PAGE_CHARS = 12_000;

export const SKILL_SCOPES = ["platform", "organization", "project"] as const;
export type SkillScope = (typeof SKILL_SCOPES)[number];

export const SKILL_STATUSES = ["draft", "active", "deprecated", "revoked"] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

export const SKILL_BINDING_MODES = ["mandatory", "available"] as const;
export type SkillBindingMode = (typeof SKILL_BINDING_MODES)[number];

export const SKILL_SOURCE_TYPES = ["project", "github", "manual", "migrated_procedure"] as const;
export type SkillSourceType = (typeof SKILL_SOURCE_TYPES)[number];

export const skillRefSchema = z
  .object({
    id: z.string().regex(SKILL_NAME_RE),
    version_id: z.uuid(),
    content_hash: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict();
export type SkillRef = z.infer<typeof skillRefSchema>;

export const skillBindingSchema = z
  .object({
    skill_version_id: z.uuid(),
    content_hash: z.string().regex(/^[0-9a-f]{64}$/),
    mode: z.enum(SKILL_BINDING_MODES).default("available"),
  })
  .strict();
export type SkillBinding = z.infer<typeof skillBindingSchema>;

export const skillManifestAssetSchema = z
  .object({
    path: z.string().min(1).max(255),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size_bytes: z.number().int().nonnegative().max(SKILL_ASSET_MAX_BYTES),
    mime: z.string().min(1).max(100),
  })
  .strict();
export type SkillManifestAsset = z.infer<typeof skillManifestAssetSchema>;

export const skillMetadataSchema = z
  .object({
    name: z.string().regex(SKILL_NAME_RE),
    description: z.string().min(1).max(SKILL_DESCRIPTION_MAX_CHARS),
    license: z.string().max(100).optional(),
    compatibility: z.string().max(500).optional(),
    disable_model_invocation: z.boolean().default(false),
    allowed_tools: z.array(z.string().max(100)).max(50).optional(),
    roles: z.array(z.enum(AGENT_ROLES)).optional(),
    task_types: z.array(z.enum(TASK_TYPES)).optional(),
    tags: z.array(z.string().min(1).max(50)).max(20).optional(),
    extra: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type SkillMetadata = z.infer<typeof skillMetadataSchema>;

export const agentSkillsConfigSchema = z
  .object({
    inherit_platform: z.boolean().default(true),
    inherit_project: z.boolean().default(true),
    bindings: z.array(skillBindingSchema).max(50).default([]),
  })
  .strict();
export type AgentSkillsConfig = z.infer<typeof agentSkillsConfigSchema>;

export interface SkillDefinition {
  id: string;
  organization_id: string;
  project_id: string | null;
  scope: SkillScope;
  name: string;
  description: string;
  active_version_id: string | null;
  status: SkillStatus;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface SkillVersion {
  id: string;
  skill_definition_id: string;
  number: number;
  content_hash: string;
  metadata: SkillMetadata;
  body_markdown: string;
  source_type: SkillSourceType;
  source_repo?: string | null;
  source_commit?: string | null;
  source_path?: string | null;
  assets: SkillManifestAsset[];
  created_by: string | null;
  created_at: string;
}

export interface SkillContextSnapshot {
  id: string;
  organization_id: string;
  project_id: string | null;
  invocation_key: string;
  manifest_hash: string;
  skills: Array<{
    ref: SkillRef;
    name: string;
    description: string;
    mode: SkillBindingMode;
    source: string;
  }>;
  created_at: string;
}

export const listSkillsInputSchema = z
  .object({
    filter: z.string().max(100).optional(),
    scope: z.enum(SKILL_SCOPES).optional(),
    role: z.enum(AGENT_ROLES).optional(),
    task_type: z.enum(TASK_TYPES).optional(),
    limit: z.number().int().positive().max(100).default(50),
    cursor: z.string().optional(),
  })
  .strict();
export type ListSkillsInput = z.infer<typeof listSkillsInputSchema>;

export const loadSkillInputSchema = z
  .object({
    skill_id: z.string().min(1).max(64),
    version_id: z.uuid().optional(),
  })
  .strict();
export type LoadSkillInput = z.infer<typeof loadSkillInputSchema>;

export const readSkillResourceInputSchema = z
  .object({
    skill_id: z.string().min(1).max(64),
    version_id: z.uuid().optional(),
    path: z.string().min(1).max(255),
    offset: z.number().int().nonnegative().default(0),
    limit: z.number().int().positive().max(SKILL_RESOURCE_READ_PAGE_CHARS).default(SKILL_RESOURCE_READ_PAGE_CHARS),
  })
  .strict();
export type ReadSkillResourceInput = z.infer<typeof readSkillResourceInputSchema>;
